import React, { useRef, useEffect, useCallback } from 'react';
import '../ComponentsCss/ResizableTable.css';

const MIN_COLUMN_WIDTH = 120;

const ResizableTable = ({
  tableId = 'default',
  children,
  className = '',
  style = {}
}) => {
  const tableRef = useRef(null);
  const isResizing = useRef(false);
  const currentIndex = useRef(null);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const frameRef = useRef(null);

  const storageKey = `susan-col-widths-${tableId}`;

  const getHeaders = useCallback(() => {
    if (!tableRef.current) return [];
    return Array.from(tableRef.current.querySelectorAll('thead th'));
  }, []);

  const getRows = useCallback(() => {
    if (!tableRef.current) return [];
    return Array.from(tableRef.current.querySelectorAll('tbody tr'));
  }, []);

  const ensureColGroup = useCallback(() => {
    if (!tableRef.current) return [];

    let colgroup = tableRef.current.querySelector('colgroup');
    const ths = getHeaders();

    if (!colgroup) {
      colgroup = document.createElement('colgroup');
      tableRef.current.insertBefore(colgroup, tableRef.current.firstChild);
    }

    while (colgroup.children.length < ths.length) {
      colgroup.appendChild(document.createElement('col'));
    }

    while (colgroup.children.length > ths.length) {
      colgroup.removeChild(colgroup.lastChild);
    }

    return Array.from(colgroup.children);
  }, [getHeaders]);

  const applyWidthAtIndex = useCallback((index, width) => {
    const cols = ensureColGroup();
    const ths = getHeaders();
    const rows = getRows();
    const safeWidth = `${Math.max(MIN_COLUMN_WIDTH, width)}px`;

    if (cols[index]) {
      cols[index].style.width = safeWidth;
      cols[index].style.minWidth = safeWidth;
      cols[index].style.maxWidth = safeWidth;
    }

    if (ths[index]) {
      ths[index].style.width = safeWidth;
      ths[index].style.minWidth = safeWidth;
      ths[index].style.maxWidth = safeWidth;
    }

    rows.forEach((row) => {
      const cell = row.children[index];
      if (cell) {
        cell.style.width = safeWidth;
        cell.style.minWidth = safeWidth;
        cell.style.maxWidth = safeWidth;
      }
    });
  }, [ensureColGroup, getHeaders, getRows]);

  const syncTableWidth = useCallback(() => {
    const cols = ensureColGroup();
    if (!cols.length || !tableRef.current) return;

    const totalWidth = cols.reduce((sum, col) => {
      const width = parseFloat(col.style.width || col.style.minWidth || '0');
      return sum + width;
    }, 0);

   if (totalWidth > 0) {
  tableRef.current.style.width = `${totalWidth}px`;
  tableRef.current.style.minWidth = '100%';
  tableRef.current.style.maxWidth = 'none';
}
  }, [ensureColGroup]);

  const freezeAllColumns = useCallback(() => {
    const ths = getHeaders();
    if (!ths.length) return;

    ths.forEach((th, index) => {
const width = Math.max(
  MIN_COLUMN_WIDTH,
  Math.ceil(
    th.offsetWidth || th.getBoundingClientRect().width
  )
);      applyWidthAtIndex(index, width);
    });

    syncTableWidth();
  }, [getHeaders, applyWidthAtIndex, syncTableWidth]);

  const saveWidths = useCallback(() => {
    const cols = ensureColGroup();
    if (!cols.length) return;

    const widths = cols.map((col) => parseFloat(col.style.width || '0'));
    try {
      localStorage.setItem(storageKey, JSON.stringify(widths));
    } catch (e) {}
  }, [ensureColGroup, storageKey]);

  const restoreWidths = useCallback(() => {
    const ths = getHeaders();
    if (!ths.length) return;

    try {
      const saved = JSON.parse(localStorage.getItem(storageKey));
      if (!saved || !Array.isArray(saved) || saved.length !== ths.length) return;

      saved.forEach((width, index) => {
        if (width) {
          applyWidthAtIndex(index, Number(width));
        }
      });

      syncTableWidth();
    } catch (e) {}
  }, [getHeaders, storageKey, applyWidthAtIndex, syncTableWidth]);

  const injectHandles = useCallback(() => {
    const ths = getHeaders();
    if (!ths.length) return;

    ths.forEach((th, index) => {
      if (index === ths.length - 1) return;
      if (th.querySelector('.resize-handle')) return;

      th.style.position = 'relative';

      const handle = document.createElement('div');
      handle.className = 'resize-handle';

      handle.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();

        freezeAllColumns();
        isResizing.current = true;
        currentIndex.current = index;
        startX.current = e.clientX;
        startWidth.current = th.getBoundingClientRect().width;

        document.body.classList.add('susan-resizing');
        handle.classList.add('active');
      });

      th.appendChild(handle);
    });
  }, [getHeaders, freezeAllColumns]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isResizing.current || currentIndex.current === null) return;

      const index = currentIndex.current;
      const diff = e.clientX - startX.current;
      const newWidth = Math.max(MIN_COLUMN_WIDTH, startWidth.current + diff);

      if (frameRef.current) cancelAnimationFrame(frameRef.current);

      frameRef.current = requestAnimationFrame(() => {
        applyWidthAtIndex(index, newWidth);
        syncTableWidth();
      });
    };

    const onMouseUp = () => {
      if (!isResizing.current) return;

      isResizing.current = false;
      document.body.classList.remove('susan-resizing');

      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }

      const ths = getHeaders();
      const index = currentIndex.current;

      if (index !== null && ths[index]) {
        const handle = ths[index].querySelector('.resize-handle');
        if (handle) handle.classList.remove('active');
      }

      currentIndex.current = null;
      saveWidths();
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [applyWidthAtIndex, getHeaders, saveWidths, syncTableWidth]);

  useEffect(() => {
    const timer = setTimeout(() => {
      freezeAllColumns();
      injectHandles();
      restoreWidths();
    }, 100);

    return () => clearTimeout(timer);
  }, [children, freezeAllColumns, injectHandles, restoreWidths]);

  return (
    <div className={`susan-table-scroll ${className}`} style={style}>
      <table ref={tableRef} className="susan-resizable-table">
        {children}
      </table>
    </div>
  );
}; 

export default ResizableTable; 